/**
 * Firebase Web SDK initialisation — used for web-platform Analytics.
 *
 * On native (iOS / Android) the @capacitor-firebase/analytics plugin handles
 * Firebase initialisation via google-services.json / GoogleService-Info.plist.
 * This module is only active on the web platform where those native files are
 * not available.
 *
 * If no VITE_FIREBASE_PROJECT_ID is set, initFirebase() is a safe no-op.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getAnalytics, type Analytics } from 'firebase/analytics'
import { ENV } from './env.js'

const firebaseConfig = {
  apiKey:            ENV.FIREBASE_API_KEY,
  authDomain:        ENV.FIREBASE_AUTH_DOMAIN,
  projectId:         ENV.FIREBASE_PROJECT_ID,
  storageBucket:     ENV.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: ENV.FIREBASE_MESSAGING_SENDER_ID,
  appId:             ENV.FIREBASE_APP_ID,
  measurementId:     ENV.FIREBASE_MEASUREMENT_ID,
}

let app: FirebaseApp | null = null
let analytics: Analytics | null = null

export function initFirebase(): void {
  if (getApps().length > 0) return
  // Only initialise if at least the projectId is configured
  if (!firebaseConfig.projectId) return
  try {
    app = initializeApp(firebaseConfig)
    if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
      analytics = getAnalytics(app)
    }
  } catch (e) {
    console.warn('[Firebase] Init failed:', e)
  }
}

export function getFirebaseAnalytics(): Analytics | null {
  return analytics
}

// ---------------------------------------------------------------------------
// Crashlytics — native only (@capacitor-firebase/crashlytics).
// On web the import resolves but calls are no-ops; any exception is swallowed.
// ---------------------------------------------------------------------------
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics'

export async function initCrashlytics(): Promise<void> {
  try {
    await FirebaseCrashlytics.setEnabled({ enabled: true })
    console.log('[Crashlytics] enabled')
  } catch (e) {
    // Not available on web — silently ignore
  }
}
